import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Card,
  Form,
  Select,
  InputNumber,
  Button,
  Typography,
  Spin,
  message,
  Space,
  Switch,
  Divider,
  Result,
  Radio,
  Input,
  Tag,
  Collapse,
  Empty
} from 'antd'
import {
  AppstoreOutlined,
  ReloadOutlined,
  SaveOutlined,
  SearchOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { useNavigate } from 'react-router-dom'
import api from '../utils/api'
import Hint from '../components/common/Hint'
import { startCommissionTour } from '../utils/appTour'

const { Title, Paragraph, Text } = Typography
const { Panel } = Collapse
const { TextArea } = Input

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 32px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const StyledCard = styled(Card)`
  border-radius: 18px;
  box-shadow: 0 18px 40px rgba(213, 114, 138, 0.12);
  border: 1px solid rgba(213, 114, 138, 0.08);

  .ant-card-head {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  span {
    color: #8c8c8c;
    font-size: 14px;
  }
`

const SearchContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const ServiceListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 600px;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d5728a;
    border-radius: 3px;
  }
`

const ServiceCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid #f4d6de;
  background: #fff5f7;
  margin-bottom: 12px;

  .ant-card-head {
    background: transparent;
    border-bottom: 1px solid #f4d6de;
    padding: 16px;
  }

  .ant-card-body {
    padding: 20px;
  }
`

const ServiceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`

const ServiceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`

const ServiceName = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #0f2562;
`

const ServiceCategory = styled.span`
  font-size: 12px;
  color: #8c8c8c;
`

const StatusTag = styled(Tag)`
  margin: 0;
`

const FormSection = styled.div`
  margin-top: 20px;
  padding: 20px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
`

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #0f2562;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f4d6de;
`

const RadioGroupWrapper = styled.div`
  margin-bottom: 20px;
`

const DoseCommissionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #fff5f7;
  border: 1px solid #f4d6de;
  border-radius: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`

const RateInputGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  flex: 1;
`

const RateLabel = styled.span`
  min-width: 100px;
  font-weight: 600;
  color: #576079;
`

const MaterialCostRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;

  @media (max-width: 480px) {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`

const InfoCard = styled(Card)`
  border-radius: 16px;
  background: #ffffffcc;
  border: 1px dashed #f0ccd4;
`

const EmptyHint = styled.div`
  padding: 16px;
  border-radius: 12px;
  background: #fafafa;
  border: 1px dashed #d9d9d9;
  color: #8c8c8c;
  text-align: center;
`

const ServiceCommissionSettingsPage = () => {
  const { user, businessPlan } = useAuth()
  const navigate = useNavigate()
  const { hasFeature, permissionsLoaded } = useMembership()
  const [services, setServices] = useState([])
  const [filteredServices, setFilteredServices] = useState([])
  const [serviceRules, setServiceRules] = useState({})
  const [categories, setCategories] = useState([])
  const [loadingServices, setLoadingServices] = useState(false)
  const [saving, setSaving] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [expandedServices, setExpandedServices] = useState(new Set())
  const [serviceForms, setServiceForms] = useState({}) // 存储每个服务的表单实例
  const [employees, setEmployees] = useState([])

  const businessId = user?.businessId

  const fetchEmployees = useCallback(async () => {
    if (!businessId) return
    try {
      const response = await api.get(`/api/employees/${businessId}`)
      setEmployees(Array.isArray(response.data) ? response.data : [])
    } catch (error) {
      console.error('取得員工清單失敗', error)
    }
  }, [businessId])

  const fetchCategories = useCallback(async () => {
    if (!businessId) return
    try {
      const response = await api.get(`/api/business_B/${businessId}/categories`)
      setCategories(response.data || [])
    } catch (error) {
      console.error('獲取類別列表失敗:', error)
    }
  }, [businessId])

  // 获取服务列表
  const fetchServices = useCallback(async () => {
    if (!businessId) return
    try {
      setLoadingServices(true)
      const response = await api.get(`/api/business_B/${businessId}/products`)
      const servicesData = Array.isArray(response.data) ? response.data : []
      setServices(servicesData)
      setFilteredServices(servicesData)
    } catch (error) {
      console.error('取得服務列表失敗', error)
      message.error('無法載入服務列表，請稍後再試')
    } finally {
      setLoadingServices(false)
    }
  }, [businessId])

  // 取得服務項目抽成規則列表
  const fetchServiceRules = useCallback(async () => {
    if (!businessId) return
    try {
      const response = await api.get(
        `/api/businesses/${businessId}/services/commission-rules`
      )
      const rules = response.data?.data?.rules || response.data?.rules || []
      const map = {}
      rules.forEach((rule) => {
        if (rule && rule.serviceId) {
          map[rule.serviceId] = rule
        }
      })
      setServiceRules(map)
    } catch (error) {
      console.error('取得服務抽成規則列表失敗', error)
      // 不阻斷主流程
    }
  }, [businessId])

  // 获取单个服务的抽成规则
  const fetchServiceCommission = useCallback(
    async (serviceId) => {
      if (!businessId || !serviceId) return null
      try {
        const response = await api.get(
          `/api/businesses/${businessId}/services/${serviceId}/commission-rule`
        )
        const ruleData =
          response.data?.data?.rule || response.data?.rule || null
        return ruleData
      } catch (error) {
        console.error('取得服務抽成規則失敗', error)
        return null
      }
    },
    [businessId]
  )

  // 初始化服务表单数据
  const initializeServiceForm = useCallback(
    async (serviceId) => {
      const rule = await fetchServiceCommission(serviceId)

      const formData = {
        isEnabled: rule?.isEnabled ?? false,
        supportDoseTypes: rule?.supportDoseTypes ?? false,
        designerRate: rule?.designerRate ?? 0,
        assistantRate: rule?.assistantRate ?? 0,
        doseCommissions:
          rule?.doseCommissions && rule.doseCommissions.length > 0
            ? rule.doseCommissions
            : [
                { doseType: 'first', rate: 0 },
                { doseType: 'second', rate: 0 }
              ],
        materialCost: rule?.materialCost ?? {
          amount: 0,
          percentage: 0
        },
        materialCostType: rule?.materialCost?.amount
          ? 'amount'
          : rule?.materialCost?.percentage
          ? 'percentage'
          : 'amount',
        performanceFormula: rule?.performanceFormula ?? 'actualCharge',
        fixedAmount: rule?.fixedAmount ?? 0,
        settlementMode: rule?.settlementMode ?? 'store',
        storeShareRate: rule?.storeShareRate ?? 30,
        employeeRates: Array.isArray(rule?.employeeRates)
          ? rule.employeeRates.map((er) => ({
              employeeId: String(er.employeeId),
              rate: Number(er.rate) || 0
            }))
          : [],
        note: rule?.note ?? ''
      }

      return formData
    },
    [fetchServiceCommission]
  )

  useEffect(() => {
    if (!hasFeature('salary')) return
    fetchServices()
    fetchCategories()
    fetchServiceRules()
    fetchEmployees()
  }, [
    businessId,
    hasFeature,
    fetchServices,
    fetchCategories,
    fetchServiceRules,
    fetchEmployees
  ])

  // 搜索和篩選
  useEffect(() => {
    let filtered = services

    if (searchText) {
      filtered = filtered.filter((service) =>
        service.name?.toLowerCase().includes(searchText.toLowerCase())
      )
    }

    if (selectedCategory) {
      filtered = filtered.filter(
        (service) => service.category === selectedCategory
      )
    }

    setFilteredServices(filtered)
  }, [services, searchText, selectedCategory])

  // 權限檢查
  if (!permissionsLoaded) {
    return (
      <PageWrapper>
        <div style={{ textAlign: 'center', padding: '100px' }}>
          <Spin size='large' tip='正在載入權限...' />
        </div>
      </PageWrapper>
    )
  }

  if (!hasFeature('salary')) {
    return (
      <PageWrapper>
        <ContentWrapper>
          <Result
            status='403'
            title='功能限制'
            subTitle='此功能需要品牌方案才能使用，請升級您的會員方案。'
            extra={
              <Button
                type='primary'
                onClick={() => navigate('/settings/membership')}
              >
                查看會員方案
              </Button>
            }
          />
        </ContentWrapper>
      </PageWrapper>
    )
  }

  // 展開服務時初始化表單資料（只負責載入資料，不負責展開/收合狀態）
  const handleServiceExpand = async (serviceId) => {
    if (serviceForms[serviceId]) return
    const formData = await initializeServiceForm(serviceId)
    if (formData) {
      setServiceForms((prev) => ({
        ...prev,
        [serviceId]: formData
      }))
    }
  }

  // 保存单个服务的抽成规则
  const handleSaveService = async (serviceId) => {
    const formData = serviceForms[serviceId]
    if (!formData) {
      message.warning('請先展開服務設定')
      return
    }

    try {
      setSaving(true)

      // 构建请求数据
      const payload = {
        isEnabled: formData.isEnabled,
        supportDoseTypes: formData.supportDoseTypes,
        settlementMode: formData.settlementMode || 'store',
        storeShareRate:
          formData.storeShareRate === null || formData.storeShareRate === undefined
            ? 30
            : Number(formData.storeShareRate)
      }

      if (formData.supportDoseTypes) {
        // 第一剂/第二剂模式：每个剂型只有一个操作人员，只需一个抽成比例
        payload.doseCommissions = formData.doseCommissions.map((dose) => ({
          doseType: dose.doseType,
          rate: Number(dose.rate) || 0
        }))
      } else {
        // 一般抽成模式
        payload.designerRate = Number(formData.designerRate) || 0
        payload.assistantRate = Number(formData.assistantRate) || 0
        payload.fixedAmount = Number(formData.fixedAmount) || 0
        const rates = (formData.employeeRates || []).filter(
          (er) => er && er.employeeId
        )
        payload.employeeRates = rates.map((er) => ({
          employeeId: er.employeeId,
          rate: Number(er.rate) || 0
        }))
      }

      // 材料成本
      if (formData.materialCostType === 'amount') {
        payload.materialCost = {
          amount: Number(formData.materialCost?.amount) || 0,
          percentage: 0
        }
      } else {
        payload.materialCost = {
          amount: 0,
          percentage: Number(formData.materialCost?.percentage) || 0
        }
      }

      // 业绩公式
      payload.performanceFormula = formData.performanceFormula || 'actualCharge'

      // 备注
      if (formData.note) {
        payload.note = formData.note
      }

      await api.put(
        `/api/businesses/${businessId}/services/${serviceId}/commission-rule`,
        payload
      )

      message.success('服務抽成規則已更新')
      // 更新列表上的狀態標籤（例：員工自收・繳回 30%）
      fetchServiceRules()
    } catch (error) {
      console.error('更新服務抽成規則失敗', error)
      message.error(
        error.response?.data?.message ||
          error.message ||
          '更新服務抽成規則失敗，請稍後再試'
      )
    } finally {
      setSaving(false)
    }
  }

  // 更新服务表单数据
  const updateServiceForm = (serviceId, field, value) => {
    setServiceForms((prev) => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        [field]: value
      }
    }))
  }

  // 更新第一剂/第二剂抽成
  const updateDoseCommission = (serviceId, index, field, value) => {
    setServiceForms((prev) => {
      const formData = prev[serviceId]
      if (!formData) return prev

      const newDoseCommissions = [...(formData.doseCommissions || [])]
      newDoseCommissions[index] = {
        ...newDoseCommissions[index],
        [field]: value
      }

      return {
        ...prev,
        [serviceId]: {
          ...formData,
          doseCommissions: newDoseCommissions
        }
      }
    })
  }

  // 渲染服务抽成设置表单
  const renderServiceForm = (service) => {
    const formData = serviceForms[service._id] || {
      isEnabled: false,
      supportDoseTypes: false,
      designerRate: 0,
      assistantRate: 0,
      doseCommissions: [
        { doseType: 'first', rate: 0 },
        { doseType: 'second', rate: 0 }
      ],
      materialCost: {
        amount: 0,
        percentage: 0
      },
      materialCostType: 'amount',
      performanceFormula: 'actualCharge',
      fixedAmount: 0,
      settlementMode: 'store',
      storeShareRate: 30,
      employeeRates: [],
      note: ''
    }
    const isSelfCollected = formData.settlementMode === 'self_collected'
    const employeeRates = formData.employeeRates || []
    const setEmployeeRates = (next) =>
      updateServiceForm(service._id, 'employeeRates', next)

    return (
      <FormSection>
        {/* 启用开关 */}
        <div style={{ marginBottom: 20 }}>
          <Space>
            <Switch
              checked={formData.isEnabled}
              onChange={(checked) =>
                updateServiceForm(service._id, 'isEnabled', checked)
              }
              checkedChildren='已啟用'
              unCheckedChildren='未啟用'
            />
            <Text>啟用此抽成規則</Text>
          </Space>
        </div>

        {/* 結算方式：店家收款／員工自收 */}
        <RadioGroupWrapper>
          <SectionTitle>結算方式</SectionTitle>
          <Radio.Group
            value={formData.settlementMode || 'store'}
            onChange={(e) =>
              updateServiceForm(service._id, 'settlementMode', e.target.value)
            }
          >
            <Space direction='vertical'>
              <Radio value='store'>店家收款，員工抽成（一般）</Radio>
              <Radio value='self_collected'>
                員工自收，繳回店家一定比例
              </Radio>
            </Space>
          </Radio.Group>
          {isSelfCollected && (
            <div style={{ marginTop: 16 }}>
              <RateInputGroup>
                <RateLabel>繳回店家</RateLabel>
                <InputNumber
                  min={0}
                  max={100}
                  value={formData.storeShareRate}
                  onChange={(value) =>
                    updateServiceForm(
                      service._id,
                      'storeShareRate',
                      value ?? 0
                    )
                  }
                  addonAfter='%'
                  style={{ width: 200 }}
                />
              </RateInputGroup>
              <div style={{ marginTop: 12 }}>
                <Hint tone='info' title='員工自收怎麼算'>
                  客人直接付給做的人，這個項目不會算進薪資與業績階梯，
                  改列在薪資明細的「自收項目拆帳」，顯示本人所得與應繳店家金額；
                  營收報表也只把繳回的 {formData.storeShareRate ?? 0}% 算成店家收入。
                  <br />
                  用堂票做的那次，以「堂票售價 ÷ 總堂數」當作這次的收費。
                  若堂票也是員工收錢，記得到堂票設定勾選「員工自收堂票」。
                </Hint>
              </div>
            </div>
          )}
        </RadioGroupWrapper>

        {!isSelfCollected && (
        <>
        {/* 抽成类型 */}
        <RadioGroupWrapper>
          <SectionTitle>抽成類型</SectionTitle>
          <Radio.Group
            value={formData.supportDoseTypes}
            onChange={(e) =>
              updateServiceForm(service._id, 'supportDoseTypes', e.target.value)
            }
          >
            <Space direction='vertical'>
              <Radio value={false}>一般抽成</Radio>
              <Radio value={true}>第一劑/第二劑（燙髮/染髮）</Radio>
            </Space>
          </Radio.Group>
        </RadioGroupWrapper>

        {/* 一般抽成设置 */}
        {!formData.supportDoseTypes && (
          <div style={{ marginTop: 20 }}>
            <SectionTitle>一般抽成設定</SectionTitle>
            <Paragraph
              style={{ color: '#8c8c8c', marginBottom: 16, fontSize: '13px' }}
            >
              💡 說明：設計師與助理「各自」按抽成比例分配
              <br />
              設計師抽成 = (業績-材料成本) × 設計師抽成%
              <br />
              助理抽成 = (業績-材料成本) × 助理抽成%
            </Paragraph>
            <Space direction='vertical' style={{ width: '100%' }} size='large'>
              <RateInputGroup>
                <RateLabel>設計師抽成</RateLabel>
                <InputNumber
                  min={0}
                  max={100}
                  value={formData.designerRate}
                  onChange={(value) =>
                    updateServiceForm(service._id, 'designerRate', value || 0)
                  }
                  addonAfter='%'
                  style={{ width: 200 }}
                />
              </RateInputGroup>
              <RateInputGroup>
                <RateLabel>助理抽成</RateLabel>
                <InputNumber
                  min={0}
                  max={100}
                  value={formData.assistantRate}
                  onChange={(value) =>
                    updateServiceForm(service._id, 'assistantRate', value || 0)
                  }
                  addonAfter='%'
                  style={{ width: 200 }}
                />
              </RateInputGroup>
              <RateInputGroup>
                <RateLabel>每次固定抽成</RateLabel>
                <InputNumber
                  min={0}
                  value={formData.fixedAmount}
                  onChange={(value) =>
                    updateServiceForm(service._id, 'fixedAmount', value || 0)
                  }
                  prefix='NT$'
                  addonAfter='/ 次'
                  style={{ width: 200 }}
                />
              </RateInputGroup>
              <Text type='secondary' style={{ fontSize: 12 }}>
                做一次就給操作的設計師這個金額（例：技術費 100 元／次），不論售價、堂票核銷也照給；可與上方 % 同時使用。
              </Text>

              <div>
                <RateLabel style={{ marginBottom: 8 }}>
                  指定人員抽成（可選）
                </RateLabel>
                <Text
                  type='secondary'
                  style={{ fontSize: 12, display: 'block', marginBottom: 8 }}
                >
                  有設定時：清單內的人用自己的比例，其他人一律用上方「設計師抽成」%（填 0 就是 0）。
                </Text>
                <Space direction='vertical' style={{ width: '100%' }}>
                  {employeeRates.map((er, idx) => (
                    <Space key={idx} wrap>
                      <Select
                        style={{ width: 180 }}
                        placeholder='選擇人員'
                        value={er.employeeId || undefined}
                        onChange={(v) =>
                          setEmployeeRates(
                            employeeRates.map((x, i) =>
                              i === idx ? { ...x, employeeId: v } : x
                            )
                          )
                        }
                        options={employees.map((emp) => ({
                          value: String(emp._id),
                          label: emp.name || emp.username || '未命名員工'
                        }))}
                      />
                      <InputNumber
                        min={0}
                        max={100}
                        value={er.rate}
                        onChange={(v) =>
                          setEmployeeRates(
                            employeeRates.map((x, i) =>
                              i === idx ? { ...x, rate: v || 0 } : x
                            )
                          )
                        }
                        addonAfter='%'
                        style={{ width: 140 }}
                      />
                      <Button
                        danger
                        type='link'
                        onClick={() =>
                          setEmployeeRates(
                            employeeRates.filter((_, i) => i !== idx)
                          )
                        }
                      >
                        移除
                      </Button>
                    </Space>
                  ))}
                  <Button
                    type='dashed'
                    onClick={() =>
                      setEmployeeRates([
                        ...employeeRates,
                        { employeeId: undefined, rate: 0 }
                      ])
                    }
                  >
                    ＋ 新增指定人員
                  </Button>
                </Space>
              </div>
            </Space>
          </div>
        )}

        {/* 第一剂/第二剂抽成设置 */}
        {formData.supportDoseTypes && (
          <div style={{ marginTop: 20 }}>
            <SectionTitle>第一劑/第二劑抽成設定</SectionTitle>
            <Paragraph
              style={{ color: '#8c8c8c', marginBottom: 16, fontSize: '13px' }}
            >
              💡 說明：第一劑和第二劑各自只有一個操作人員，只需設定一個抽成比例
              <br />
              例如：第一劑7%由A操作，第二劑5%由B操作
              <br />
              計算公式：操作人員抽成 = (業績-材料成本) × rate%
            </Paragraph>
            {formData.doseCommissions?.map((dose, index) => (
              <DoseCommissionRow key={dose.doseType || index}>
                <RateLabel>
                  {dose.doseType === 'first' ? '第一劑' : '第二劑'}
                </RateLabel>
                <RateInputGroup>
                  <Text style={{ minWidth: 120 }}>
                    抽成比例（由操作人員執行）
                  </Text>
                  <InputNumber
                    min={0}
                    max={100}
                    value={dose.rate}
                    onChange={(value) =>
                      updateDoseCommission(
                        service._id,
                        index,
                        'rate',
                        value || 0
                      )
                    }
                    addonAfter='%'
                    style={{ width: 200 }}
                  />
                </RateInputGroup>
              </DoseCommissionRow>
            ))}
          </div>
        )}

        <Divider />

        {/* 材料成本设定 */}
        <div style={{ marginTop: 20 }}>
          <SectionTitle>材料成本設定（可選）</SectionTitle>
          <MaterialCostRow>
            <Radio.Group
              value={formData.materialCostType}
              onChange={(e) =>
                updateServiceForm(
                  service._id,
                  'materialCostType',
                  e.target.value
                )
              }
            >
              <Radio value='amount'>固定金額</Radio>
              <Radio value='percentage'>比例</Radio>
            </Radio.Group>
            {formData.materialCostType === 'amount' ? (
              <InputNumber
                min={0}
                value={formData.materialCost?.amount || 0}
                onChange={(value) =>
                  updateServiceForm(service._id, 'materialCost', {
                    ...formData.materialCost,
                    amount: value || 0,
                    percentage: 0
                  })
                }
                prefix='NT$'
                style={{ width: 200 }}
              />
            ) : (
              <InputNumber
                min={0}
                max={100}
                value={formData.materialCost?.percentage || 0}
                onChange={(value) =>
                  updateServiceForm(service._id, 'materialCost', {
                    ...formData.materialCost,
                    amount: 0,
                    percentage: value || 0
                  })
                }
                addonAfter='%'
                style={{ width: 200 }}
              />
            )}
          </MaterialCostRow>
        </div>

        <Divider />

        {/* 业绩公式 */}
        <div style={{ marginTop: 20 }}>
          <SectionTitle>業績公式</SectionTitle>
          <Radio.Group
            value={formData.performanceFormula}
            onChange={(e) =>
              updateServiceForm(
                service._id,
                'performanceFormula',
                e.target.value
              )
            }
          >
            <Space direction='vertical'>
              <Radio value='actualCharge'>實際收費（預設）</Radio>
              <Radio value='originalPrice'>原始價格</Radio>
              <Radio value='afterMaterialCost'>扣除材料成本後</Radio>
            </Space>
          </Radio.Group>
        </div>
        </>
        )}

        <Divider />

        {/* 备注 */}
        <div style={{ marginTop: 20 }}>
          <SectionTitle>備註</SectionTitle>
          <TextArea
            rows={3}
            value={formData.note || ''}
            onChange={(e) =>
              updateServiceForm(service._id, 'note', e.target.value)
            }
            placeholder='請輸入備註'
          />
        </div>

        {/* 保存按钮 */}
        <Actions>
          <Button
            type='primary'
            icon={<SaveOutlined />}
            onClick={() => handleSaveService(service._id)}
            loading={saving}
          >
            儲存設定
          </Button>
        </Actions>
      </FormSection>
    )
  }

  return (
    <PageWrapper>
      <ContentWrapper>
        <StyledCard
          title={
            <Header>
              <Title level={3} style={{ marginBottom: 0, color: '#0f2562' }}>
                服務項目抽成規則設定
              </Title>
              <span>
                為每個服務項目設定獨立的抽成規則，支援第一劑/第二劑、材料成本、不同業績公式等進階設定。
              </span>
            </Header>
          }
        >
          <div data-tour='service-rules-intro'>
            <Paragraph style={{ color: '#576079', marginBottom: 16 }}>
              <Button
                type='link'
                onClick={() => startCommissionTour(navigate, 4)}
                style={{ padding: 0, marginRight: 8 }}
              >
                觀看抽成設定教學
              </Button>
            </Paragraph>
            <Paragraph style={{ color: '#576079', marginBottom: 24 }}>
              選擇服務項目後，即可設定該服務的抽成規則。支援一般抽成或第一劑/第二劑抽成模式。
              <br />
              <Text type='secondary' style={{ fontSize: '12px' }}>
                💡 優先順序：有設定本規則的服務以本規則為準（含材料費、設計師／助理趴數）；未設定的服務才改套用「員工技術服務業績抽成」的階梯抽成與是否先扣材料費。
              </Text>
            </Paragraph>
          </div>

          {/* 搜索和筛选 */}
          <SearchContainer data-tour='service-rules-search'>
            <Input
              placeholder='搜尋服務名稱'
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ flex: 1, maxWidth: 300 }}
              allowClear
            />
            <Select
              placeholder='選擇分類'
              value={selectedCategory}
              onChange={setSelectedCategory}
              allowClear
              style={{ width: 200 }}
            >
              {categories.map((category) => (
                <Select.Option key={category._id} value={category._id}>
                  {category.name}
                </Select.Option>
              ))}
            </Select>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchServices}
              loading={loadingServices}
            >
              重新載入
            </Button>
          </SearchContainer>

          {/* 服务列表 */}
          {loadingServices ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size='large' tip='載入服務列表中...' />
            </div>
          ) : filteredServices.length === 0 ? (
            <Empty description='暫無服務項目' />
          ) : (
            <ServiceListContainer>
              <Collapse
                activeKey={Array.from(expandedServices)}
                onChange={(keys) => {
                  const keyArray = Array.isArray(keys) ? keys : [keys]
                  const newSet = new Set(keyArray)
                  // 對於新展開的 service 初始化其抽成表單資料
                  keyArray.forEach((key) => {
                    if (!expandedServices.has(key)) {
                      handleServiceExpand(key)
                    }
                  })
                  setExpandedServices(newSet)
                }}
                expandIconPosition='end'
              >
                {filteredServices.map((service) => {
                  const rule = serviceRules[service._id]
                  const formData = serviceForms[service._id]

                  let statusText = '未設定'
                  let statusColor = 'default'

                  // 優先使用後端回傳的規則，其次使用已載入的表單 isEnabled 狀態
                  if (rule) {
                    if (rule.isEnabled && rule.settlementMode === 'self_collected') {
                      statusText = `員工自收・繳回 ${rule.storeShareRate ?? 30}%`
                      statusColor = 'purple'
                    } else if (rule.isEnabled) {
                      statusText = '已啟用'
                      statusColor = 'green'
                    } else {
                      statusText = '已停用'
                      statusColor = 'default'
                    }
                  } else if (formData) {
                    if (formData.isEnabled) {
                      statusText = '已啟用'
                      statusColor = 'green'
                    } else {
                      statusText = '已停用'
                      statusColor = 'default'
                    }
                  }

                  return (
                    <Panel
                      key={service._id}
                      header={
                        <ServiceHeader>
                          <ServiceInfo>
                            <ServiceName>
                              {service.name || '未命名服務'}
                            </ServiceName>
                            <ServiceCategory>
                              分類：
                              {categories.find(
                                (c) => c._id === service.category
                              )?.name || '未分類'}
                            </ServiceCategory>
                          </ServiceInfo>
                          <StatusTag color={statusColor}>
                            {statusText}
                          </StatusTag>
                        </ServiceHeader>
                      }
                    >
                      {renderServiceForm(service)}
                    </Panel>
                  )
                })}
              </Collapse>
            </ServiceListContainer>
          )}
        </StyledCard>

        <InfoCard>
          <Title level={5} style={{ color: '#0f2562', marginBottom: 12 }}>
            設定建議
          </Title>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>
              •
              第一劑/第二劑模式適用於燙髮、染髮等需要分階段處理的服務。每個劑型只有一個操作人員，只需設定一個抽成比例。
            </Text>
          </Paragraph>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>
              •
              材料成本可設定固定金額或比例，系統會根據業績公式計算抽成基礎。如果同時設定固定金額和比例，優先使用固定金額。
            </Text>
          </Paragraph>
          <Paragraph style={{ marginBottom: 6 }}>
            <Text strong>
              •
              設計師與助理各自按抽成比例分配，一個項目兩個人做，就按各自抽成分。
            </Text>
          </Paragraph>
        </InfoCard>
      </ContentWrapper>
    </PageWrapper>
  )
}

export default ServiceCommissionSettingsPage
